import styles from "./stepper.module.css";
import Confirmation from "./Steps/Confirmation/Confirmation";
import Filial from "./Steps/Filial/Filial";
import Propietario from "./Steps/Owner/Owner";
import Autorizados from "./Steps/Authorized/Authorized";
import { useResidents } from "../../hooks/useResidents";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  validateFilial,
  validatePropietario,
  validateClienteIdentidad,
  validateClienteContacto,
} from "../../services/validationRules";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../shared/i18n/useLang";
import {
  crearResidenteCompleto,
  actualizarResidenteCompleto,
  obtenerResidenteCompleto,
} from "../../services/residentService";

// Construye los pasos del wizard según el tipo de organización.
//   - condominio: Filial → Propietario → Autorizados → Confirmación
//   - cliente:    Identificación → Contacto → Confirmación (ID automático)
function buildSteps(esCliente, L) {
  if (esCliente) {
    return [
      { numStep: 0, title: L("Identificación", "Identification"), component: Propietario, props: { section: "identidad" }, validate: validateClienteIdentidad, key: "propietario" },
      { numStep: 1, title: L("Contacto", "Contact"),              component: Propietario, props: { section: "contacto" },  validate: validateClienteContacto,  key: "propietario" },
      { numStep: 2, title: L("Confirmación", "Confirmation"),     component: Confirmation },
    ];
  }
  return [
    { numStep: 0, title: L("Filial", "Unit"),            component: Filial,      validate: validateFilial,      key: "filial" },
    { numStep: 1, title: L("Propietario", "Owner"),      component: Propietario, validate: validatePropietario, key: "propietario" },
    { numStep: 2, title: L("Autorizados", "Authorized"), component: Autorizados },
    { numStep: 3, title: L("Confirmación", "Confirmation"), component: Confirmation },
  ];
}

// Errores de validación del backend ("filial.numFolio", "propietario.vehiculos.0"...)
// -> paso del asistente donde se corrigen y errores por campo de ese paso.
function erroresDelBackend(errores) {
  const claves = Object.keys(errores || {});
  if (!claves.length) return { paso: null, campos: {} };
  const seccion = (k) => k.split(".")[0];
  const paso = claves.some((k) => seccion(k) === "filial") ? 0
    : claves.some((k) => ["propietario", "copropietarios"].includes(seccion(k))) ? 1
    : claves.some((k) => seccion(k) === "autorizados") ? 2
    : null;
  const NOMBRES = { tipo_propiedad: "tipoPropiedad", emails: "emailPrincipal" };
  const campos = {};
  claves.forEach((k) => {
    const [sec, campo, idx] = k.split(".");
    if (!((paso === 0 && sec === "filial") || (paso === 1 && sec === "propietario"))) return;
    if (campo === "vehiculos") {
      campos.vehiculos = campos.vehiculos || [];
      campos.vehiculos[Number(idx)] = errores[k];
    } else {
      campos[NOMBRES[campo] || campo] = errores[k];
    }
  });
  return { paso, campos };
}

const INITIAL_FORM = {
  filial: {
    idFilial: "",
    numPlano: "",
    numFolio: "",
    tipoPropiedad: "",
  },
  propietario: {
    cedula: "",
    tipo_cedula: "",
    nombre: "",
    apellidos: "",
    nombre_sociedad: "",
    telefono_casa: "",
    telefono_cel: "",
    telefono_trabajo: "",
    es_sociedad: false,
    vehiculos: [],
    notas: "",
    sin_correo: false,
    emailPrincipal: "",
    emailExtras: [],
    direccion_provincia: "",
    direccion_canton: "",
    direccion_distrito: "",
    direccion_senas: "",
  },
  copropietarios: [],
  autorizados: [],
};

// filialId: si viene, el asistente edita esa filial (modo condominio) con
// todos sus datos cargados, y al final muestra un resumen de los cambios.
export default function Stepper({ filialId = null }) {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const { createResident } = useResidents();
  const esCliente = useEsCliente();
  const L = useLang();
  const esEdicion = !!filialId;

  const STEPS = useMemo(() => buildSteps(esCliente, L), [esCliente, L]);

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [original, setOriginal] = useState(null); // datos guardados, para el resumen de cambios
  const [cargando, setCargando] = useState(esEdicion);
  const [errorCarga, setErrorCarga] = useState("");
  const [errors, setErrors] = useState({}); // errores del paso actual

  const progressRef = useRef(null);

  useEffect(() => {
    if (!esEdicion) return;
    let cancelado = false;
    setCargando(true);
    obtenerResidenteCompleto(filialId)
      .then((data) => {
        if (cancelado) return;
        setFormData(data);
        setOriginal(JSON.parse(JSON.stringify(data)));
      })
      .catch((e) => !cancelado && setErrorCarga(e.message))
      .finally(() => !cancelado && setCargando(false));
    return () => { cancelado = true; };
  }, [esEdicion, filialId]);

  useEffect(() => {
    if (!progressRef.current) return;
    const width = activeStep / (STEPS.length - 1);
    progressRef.current.style.transform = `scaleX(${width})`;
  }, [activeStep, STEPS.length, cargando]);

  // ── Validar el paso actual ────────────────────────────────────────────────
  function validateStep(step) {
    const def = STEPS[step];
    if (def?.validate && def?.key) return def.validate(formData[def.key]);
    return {}; // pasos opcionales / confirmación
  }

  function handleNext(e) {
    e.preventDefault();
    const errs = validateStep(activeStep);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scroll hacia el primer error
      setTimeout(() => {
        const el = document.querySelector("[data-has-error='true']");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setErrors({});
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function handlePrev(e) {
    e.preventDefault();
    setErrors({});
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLoading) return;
    setErrors({});
    try {
      setLoading(true);
      if (esCliente) await createResident(formData);            // modo cliente: flujo de siempre
      else if (esEdicion) await actualizarResidenteCompleto(filialId, formData);
      else await crearResidenteCompleto(formData);
      navigate("/residentes");
    } catch (err) {
      console.error("Fallo la secuencia de creación:", err);
      const { paso, campos } = erroresDelBackend(err.errores);
      if (paso !== null) setActiveStep(paso);
      setErrors({ _global: err.message || 'Error al guardar. Revisá los datos e intentá de nuevo.', ...campos });
    } finally {
      setLoading(false);
    }
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{L("Inicio", "Home")} </span> /
        </Link>
        <Link to="/residentes">
          <span className={styles.inactive}> {esCliente ? L("Clientes", "Clients") : L("Residentes", "Residents")} </span>/
        </Link>
        <span className={styles.active}> {esEdicion ? L("Editar", "Edit") : L("Agregar", "Add")}</span>
      </span>

      <div className={styles.stepper} style={{ marginTop: "10px" }}>
        <form className={`${styles["form-wizard"]} ${!esCliente ? styles.wide : ""}`}>
          <h1 className="title">
            {esEdicion
              ? `${L("Editar residente", "Edit resident")}${original ? ` - ${original.filial.idFilial}` : ""}`
              : L("Registración", "Registration")}
          </h1>

          {cargando || errorCarga ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: errorCarga ? "#f87171" : "var(--text-muted)" }}>
              {errorCarga ? (
                <>
                  <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6, color: "inherit" }} />
                  {errorCarga} <Link to="/residentes">{L("Volver", "Back")}</Link>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6, color: "#f97316" }} />
                  {L("Cargando datos…", "Loading…")}
                </>
              )}
            </div>
          ) : (
          <>

          {/* Barra de progreso */}
          <div className={styles["progress-container"]}>
            <div ref={progressRef} className={styles.progress} />
            <ol>
              {STEPS.map((step) => (
                <li
                  key={step.title}
                  className={
                    activeStep === step.numStep
                      ? styles.active
                      : activeStep > step.numStep
                        ? styles.done
                        : ""
                  }
                >
                  <span>{step.title}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Banner de errores globales del paso */}
          {hasErrors && (
            <div
              style={{
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.35)",
                borderLeft: "4px solid #ef4444",
                borderRadius: 8,
                padding: "10px 16px",
                margin: "0 0 12px",
                color: "#f87171",
                fontSize: "0.83rem",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="fa-solid fa-triangle-exclamation" />
              {errors._global || L("Hay campos con errores. Revísalos antes de continuar.",
                 "Some fields have errors. Review them before continuing.")}
            </div>
          )}

          {/* Step actual */}
          <div className={styles["steps-container"]}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
                style={{ width: "100%" }}
              >
                {(() => {
                  const Component = STEPS[activeStep].component;
                  return (
                    <Component
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      original={original}
                      {...(STEPS[activeStep].props || {})}
                    />
                  );
                })()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controles */}
          <div className={styles.controls}>
            <button
              type="button"
              className={styles.btn}
              onClick={handlePrev}
              disabled={activeStep === 0}
            >
              {L("Atrás", "Back")}
            </button>

            {activeStep < STEPS.length - 1 ? (
              <button
                type="button"
                className={styles.btn}
                onClick={handleNext}
                style={{
                  background: hasErrors ? "rgba(239,68,68,0.15)" : undefined,
                  borderColor: hasErrors ? "rgba(239,68,68,0.5)" : undefined,
                }}
              >
                {hasErrors ? (
                  <>
                    <i
                      className="fa-solid fa-circle-exclamation"
                      style={{ marginRight: 5 }}
                    />
                    {L("Corregir errores", "Fix errors")}
                  </>
                ) : (
                  L("Siguiente →", "Next →")
                )}
              </button>
            ) : (
              <button
                type="submit"
                className={styles.btn}
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <>
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: 5 }}
                    />
                    {L("Guardando…", "Saving…")}
                  </>
                ) : esEdicion ? (
                  L("Guardar cambios", "Save changes")
                ) : (
                  esCliente ? L("Guardar Cliente", "Save Client") : L("Guardar Residente", "Save Resident")
                )}
              </button>
            )}
          </div>
          </>
          )}
        </form>
      </div>
    </>
  );
}
